import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageBackground from '../components/PageBackgroundBeam';
import PageFooter from '../components/PageFooter';
import './PageOrigenInversion2.css';

function formatNumber(n) {
  return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

const COLORS = { 1: '#4ade80', 2: '#93c5fd', 3: '#fbbf24', 4: '#f87171' };
const LABELS = { 1: 'DOM', 2: 'TRANS', 3: 'GOB', 4: 'POL' };

const ALL_YEARS = [
  { year: 2020, data: [
    { id: 1, value: 166847531 },
    { id: 2, value: 89831618 },
    { id: 3, value: 25417348 },
    { id: 4, value: 99365 },
  ]},
  { year: 2021, data: [
    { id: 1, value: 205863683 },
    { id: 2, value: 102582388 },
    { id: 3, value: 40887060 },
    { id: 4, value: 12155155 },
  ]},
  { year: 2022, data: [
    { id: 1, value: 193051785 },
    { id: 2, value: 111078086 },
    { id: 3, value: 22038088 },
    { id: 4, value: 126163 },
  ]},
  { year: 2023, data: [
    { id: 1, value: 230195373 },
    { id: 2, value: 102383141 },
    { id: 3, value: 16157661 },
    { id: 4, value: 6223 },
  ]},
  { year: 2024, data: [
    { id: 1, value: 281406806 },
    { id: 2, value: 126057161 },
    { id: 3, value: 16967091 },
    { id: 4, value: 218417 },
  ]},
  { year: 2025, data: [
    { id: 1, value: 278372312 },
    { id: 2, value: 133818689 },
    { id: 3, value: 11487116 },
    { id: 4, value: 10465765 },
  ]},
];

function PieChart({ data, year, size = 240 }) {
  const [hovered, setHovered] = useState(null);
  const total = data.reduce((s, d) => s + d.value, 0);
  const cx = size / 2;
  const cy = size / 2;
  const radius = size / 2 - 6;
  const innerRadius = radius * 0.55;

  let cumAngle = -Math.PI / 2;
  const slices = data.map((d) => {
    const pct = d.value / total;
    const angle = pct * 2 * Math.PI;
    const startAngle = cumAngle;
    const endAngle = cumAngle + angle;
    cumAngle = endAngle;

    const x1 = cx + radius * Math.cos(startAngle);
    const y1 = cy + radius * Math.sin(startAngle);
    const x2 = cx + radius * Math.cos(endAngle);
    const y2 = cy + radius * Math.sin(endAngle);
    const ix1 = cx + innerRadius * Math.cos(endAngle);
    const iy1 = cy + innerRadius * Math.sin(endAngle);
    const ix2 = cx + innerRadius * Math.cos(startAngle);
    const iy2 = cy + innerRadius * Math.sin(startAngle);
    const largeArc = angle > Math.PI ? 1 : 0;

    const path = [
      `M ${x1} ${y1}`,
      `A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`,
      `L ${ix1} ${iy1}`,
      `A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${ix2} ${iy2}`,
      'Z',
    ].join(' ');

    const midAngle = startAngle + angle / 2;
    const labelR = (radius + innerRadius) / 2;
    const lx = cx + labelR * Math.cos(midAngle);
    const ly = cy + labelR * Math.sin(midAngle);

    return { ...d, pct, path, lx, ly, color: COLORS[d.id] };
  });

  return (
    <div className="pie-card">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="pie-svg">
        <defs>
          {slices.map((s) => (
            <filter key={s.id} id={`g6-${year}-${s.id}`}>
              <feDropShadow dx="0" dy="0" stdDeviation="5" floodColor={s.color} floodOpacity="0.35" />
            </filter>
          ))}
        </defs>
        {slices.map((s) => (
          <path
            key={s.id}
            d={s.path}
            fill={s.color}
            fillOpacity={hovered === s.id ? 1 : 0.85}
            stroke={hovered === s.id ? '#fff' : 'rgba(15,23,42,0.8)'}
            strokeWidth={hovered === s.id ? 2.5 : 2}
            filter={`url(#g6-${year}-${s.id})`}
            className="pie-slice"
            onMouseEnter={() => setHovered(s.id)}
            onMouseLeave={() => setHovered(null)}
          />
        ))}
        {slices.map((s) => {
          const show = hovered === s.id;
          const labelR = radius + 18;
          const midAngle = Math.atan2(s.ly - cy, s.lx - cx);
          const tx = cx + labelR * Math.cos(midAngle);
          const ty = cy + labelR * Math.sin(midAngle);
          return show ? (
            <g key={`tip-${s.id}`} className="pie-tooltip-g" pointerEvents="none">
              <rect
                x={tx - 32} y={ty - 12}
                width="64" height="24"
                rx="6"
                fill="rgba(15,23,42,0.92)"
                stroke={s.color}
                strokeWidth="1"
              />
              <text x={tx} y={ty + 1} textAnchor="middle" dominantBaseline="middle" className="pie-tooltip-text" fill={s.color}>
                {LABELS[s.id]} {(s.pct * 100).toFixed(1)}%
              </text>
            </g>
          ) : null;
        })}
        <text x={cx} y={cy - 8} textAnchor="middle" dominantBaseline="middle" className="pie-center-year">
          {year}
        </text>
        <text x={cx} y={cy + 12} textAnchor="middle" dominantBaseline="middle" className="pie-center-total">
          ${formatNumber(total)}
        </text>
      </svg>
    </div>
  );
}

function PageOrigenInversion2() {
  const navigate = useNavigate();

  return (
    <PageBackground>
      <div className="origen2-page">
        <header className="origen2-header">
          <button className="btn-back" onClick={() => navigate('/origen-inversion')}>
            &#8592; Origen
          </button>
          <div className="origen2-title-block">
            <h1 className="origen2-title">Origen del Capital &middot; 6 A&ntilde;os</h1>
            <p className="origen2-subtitle">Evoluci&oacute;n de la distribuci&oacute;n por origen &middot; Honduras 2020–2025 &middot; USD</p>
          </div>
          <button className="btn-next" onClick={() => navigate('/industria-anio')}>
            Siguiente &#8594;
          </button>
          <img src={require('../assets/logo-publisearch.png')} alt="Publisearch" className="header-logo-inline" onClick={() => navigate('/')} style={{ cursor: 'pointer' }} />
        </header>

        <div className="origen2-legend">
          {Object.keys(COLORS).map((id) => (
            <span key={id} className="legend-item">
              <span className="legend-dot" style={{ background: COLORS[id] }}></span>
              {LABELS[id]}
            </span>
          ))}
        </div>

        <div className="origen2-grid">
          {ALL_YEARS.map((y) => (
            <PieChart key={y.year} data={y.data} year={y.year} size={240} />
          ))}
        </div>

        <PageFooter />
      </div>
    </PageBackground>
  );
}

export default PageOrigenInversion2;
